import { Box, CardMedia } from '@mui/material';
import { useSiblings } from './useSiblings';
import { formatSiblingFirstName } from './siblingProfile';

/**
 * Props for {@link ChildNameHeading}.
 * title         - page name shown after the child's name, e.g. "Dashboard", "Billing"
 * fallbackTitle - MM-1132: text shown instead of the plain title when no first name is set,
 *                 e.g. "Child’s Profile"; defaults to title
 */
interface ChildNameHeadingProps {
  title: string;
  fallbackTitle?: string;
}

/**
 * MM-1123: page heading naming the child whose profile is open, e.g. "Emma's Billing", with the
 * yellow underline beneath the child's name. When no first name was entered at sign-up it shows the
 * fallback title, or the plain title ("Billing") when none is given. Follows every child switch
 * because the name comes from the active sibling.
 * Render inside the page's heading Typography; it supplies only the text.
 */
const ChildNameHeading = ({ title, fallbackTitle }: ChildNameHeadingProps) => {
  const { activeSibling } = useSiblings();
  /** Open child's first name; empty while loading or when no name has been entered yet. */
  const firstName = formatSiblingFirstName(activeSibling).trim();

  if (firstName === '') {
    return <>{fallbackTitle ?? title}</>;
  }
  return (
    <>
      <Box component='span' sx={{ position: 'relative', display: 'inline-block' }}>
        {firstName}
        <CardMedia
          component={'img'}
          src='/images/vector.svg'
          alt=''
          sx={{ position: 'absolute', left: 0, top: '100%', width: '100%', minWidth: 40, height: 'auto' }}
        />
      </Box>
      {`’s ${title}`}
    </>
  );
};

export default ChildNameHeading;
